import { useEffect, useRef, useState } from "react";
import { ItemModel } from "../models/ItemModel";
import itemService from "../services/itemService";

interface EditBulkDiscountCompProps {
  show: boolean;
  item: ItemModel;
  onClose: () => void;
  onBulkDiscountUpdated: (updatedItem: ItemModel) => void;
}

interface BulkDiscountFormErrors {
  bulkMinQty?: string;
  bulkDiscount?: string;
  bulkDiscountEnabled?: string;
}

const EditBulkDiscountComp: React.FC<EditBulkDiscountCompProps> = ({
  show,
  item,
  onClose,
  onBulkDiscountUpdated,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const bsModalRef = useRef<any>(null);

  // State: allow string | number for inputs so they can be cleared
  const [isBulkDiscountEnabled, setIsBulkDiscountEnabled] = useState(item.bulkDiscountEnabled);
  const [minQty, setMinQty] = useState<string | number>(item.bulkMinQty ?? '');
  const [discount, setDiscount] = useState<string | number>(item.bulkDiscount ?? '');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkDiscountFormErrors, setBulkDiscountFormErrors] = useState<BulkDiscountFormErrors>({});
  const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    if (modalRef.current) {
      bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
      modalRef.current.addEventListener("hidden.bs.modal", onClose);
    }
    return () => {
      modalRef.current?.removeEventListener("hidden.bs.modal", onClose);
    };
  }, []);

  useEffect(() => {
    if (show) {
      setIsBulkDiscountEnabled(item.bulkDiscountEnabled);
      setMinQty(item.bulkMinQty ?? '');
      setDiscount(item.bulkDiscount ?? '');
      setServiceStatus({ type: '', message: '' });
      bsModalRef.current?.show();
    } else {
      bsModalRef.current?.hide();
    }
  }, [show]);

  const validateBulkDiscount = () => {
    const errors: BulkDiscountFormErrors = {};
    if (isBulkDiscountEnabled) {
      // Parse to numbers for validation
      const minQtyNum = typeof minQty === 'string' ? Number(minQty) : minQty;
      const discountNum = typeof discount === 'string' ? Number(discount) : discount;

      if (!minQty || minQtyNum <= 0 || isNaN(minQtyNum)) {
        errors.bulkMinQty = "Minimum quantity must be greater than 0";
      }
      if (
        discount === '' ||
        discount === null ||
        isNaN(discountNum) ||
        discountNum < 1 ||
        discountNum > 99
      ) {
        errors.bulkDiscount = "Discount must be a number between 1 and 99";
      }
    }
    setBulkDiscountFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBulkDiscountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServiceStatus({ type: "", message: "" });

    if (validateBulkDiscount()) {
      setIsSubmitting(true);
      try {
        // Prepare values as numbers before sending
        const updatedItem = {
          ...item,
          bulkDiscountEnabled: isBulkDiscountEnabled,
          bulkMinQty: typeof minQty === "string" ? Number(minQty) : minQty,
          bulkDiscount: typeof discount === "string" ? Number(discount) : discount,
        };

        const updateSucceed = await itemService.updateItem(updatedItem);
        if (updateSucceed === "Success") {
          onBulkDiscountUpdated(updatedItem);
          setServiceStatus({ type: "success", message: "Bulk discount updated successfully." });
        } else {
          setServiceStatus({ type: "error", message: "Failed to update bulk discount." });
        }
      } catch (error) {
        setServiceStatus({ type: "error", message: "Failed to update bulk discount." });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBulkDiscountFeatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsBulkDiscountEnabled(checked);
    if (!checked) {
      setMinQty('');
      setDiscount('');
      setBulkDiscountFormErrors({});
      setServiceStatus({ type: '', message: '' });
    }
  };

  return (
    <div
      className="modal fade"
      tabIndex={-1}
      ref={modalRef}
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog">
        <div className="modal-content rounded-0">
          <form onSubmit={handleBulkDiscountSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">Edit Bulk Discount</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => bsModalRef.current?.hide()}
              ></button>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Bulk Discount</label>
                  <div className="form-check form-switch fs-4 ms-2 mb-4">
                    <input
                      className="form-check-input fs-3"
                      type="checkbox"
                      id="bulkDiscountSwitch"
                      checked={isBulkDiscountEnabled}
                      onChange={handleBulkDiscountFeatureChange}
                    />
                  </div>
                </div>
                {isBulkDiscountEnabled && (
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="inputMinQty" className="form-label">
                        Qty
                      </label>
                      <input
                        type="number"
                        className={`form-control ${bulkDiscountFormErrors.bulkMinQty ? "is-invalid" : ""
                          }`}
                        min={1}
                        step="1"
                        value={minQty === null ? "" : minQty}
                        onChange={(e) => setMinQty(e.target.value === '' ? '' : Number(e.target.value))}
                        id="inputMinQty"
                      />
                      {bulkDiscountFormErrors.bulkMinQty && (
                        <div className="invalid-feedback">{bulkDiscountFormErrors.bulkMinQty}</div>
                      )}
                    </div>
                    <div className="col-md-6 mb-1">
                      <label htmlFor="inputBulkDiscount" className="form-label">
                        Discount <small className="text-muted">(1–99%)</small>
                      </label>
                      <div className="input-group">
                        <input
                          type="number"
                          className={`form-control ${bulkDiscountFormErrors.bulkDiscount ? "is-invalid" : ""
                            }`}
                          min={1}
                          max={99}
                          step="1"
                          placeholder="Enter discount"
                          value={discount === null ? "" : discount}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "") {
                              setDiscount("");
                            } else {
                              const num = Number(val);
                              if (num >= 1 && num <= 99) {
                                setDiscount(num);
                              }
                            }
                          }}
                          id="inputBulkDiscount"
                        />
                        <span className="input-group-text">%</span>
                        {bulkDiscountFormErrors.bulkDiscount && (
                          <div className="invalid-feedback d-block">
                            {bulkDiscountFormErrors.bulkDiscount}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                </div>
              {serviceStatus.type.toLowerCase() === "error" && (
                <div className="col-md-12">
                  <div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div>
                </div>
              )}
              {serviceStatus.type.toLowerCase() === "success" && (
                <div className="col-md-12">
                  <div className="alert alert-success p-2 mb-0">{serviceStatus.message}</div>
                </div>
              )}
            </div>
            <div className="modal-footer d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-outline-dark rounded-0"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-dark rounded-0"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>{" "}
                    Saving ...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditBulkDiscountComp;
